import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const PaymentSetting = ({ themeId, themePageMaster }) => {
  // 상태 관리
  const [paymentPage, setPaymentPage] = useState({
    themePageMaster: themePageMaster,
    languageCode: 'ko',
    messageText: '결제 수단을 선택해주세요',
    messageColor: '#FFFFFF',
    totalAmountText: '총 금액',
    totalAmountColor: '#FFFFFF',
    priceDisplayColor: '#FFFF00',
    timerSeconds: 30,
    timerTimeoutRedirectUrl: 'start',
    timerDisplayColor: '#FF0000',
    cashPaymentEnabled: true,
    cardPaymentEnabled: true,
    couponPaymentEnabled: false,
    qrPaymentEnabled: false
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchPaymentPage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await designApi.getPaymentPage(themeId);
        
        console.log("API 응답 데이터:", data);
        
        setPaymentPage({
          ...paymentPage,
          ...data,
          // 기본값 보장
          languageCode: data.languageCode || 'ko',
          messageText: data.messageText || '결제 수단을 선택해주세요',
          messageColor: data.messageColor || '#FFFFFF',
          totalAmountText: data.totalAmountText || '총 금액',
          totalAmountColor: data.totalAmountColor || '#FFFFFF',
          priceDisplayColor: data.priceDisplayColor || '#FFFF00',
          timerSeconds: data.timerSeconds || 30,
          timerTimeoutRedirectUrl: data.timerTimeoutRedirectUrl || 'start',
          timerDisplayColor: data.timerDisplayColor || '#FF0000',
          cashPaymentEnabled: data.cashPaymentEnabled !== undefined ? data.cashPaymentEnabled : true,
          cardPaymentEnabled: data.cardPaymentEnabled !== undefined ? data.cardPaymentEnabled : true,
          couponPaymentEnabled: data.couponPaymentEnabled !== undefined ? data.couponPaymentEnabled : false,
          qrPaymentEnabled: data.qrPaymentEnabled !== undefined ? data.qrPaymentEnabled : false
        });

        console.log("최종 상태 설정:", {
          ...paymentPage,
          ...data,
          languageCode: data.languageCode || 'ko',
          cashPaymentEnabled: data.cashPaymentEnabled !== undefined ? data.cashPaymentEnabled : true,
          cardPaymentEnabled: data.cardPaymentEnabled !== undefined ? data.cardPaymentEnabled : true,
          couponPaymentEnabled: data.couponPaymentEnabled !== undefined ? data.couponPaymentEnabled : false,
          qrPaymentEnabled: data.qrPaymentEnabled !== undefined ? data.qrPaymentEnabled : false
        });
      } catch (err) {
        console.error("API 호출 실패:", err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentPage();
  }, [themeId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    console.log(`입력값 변경: ${field} = ${value}`);
    setPaymentPage(prev => {
      const newState = {
        ...prev,
        [field]: value
      };
      console.log('새로운 상태:', newState);
      return newState;
    });
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setPaymentPage({
      ...paymentPage,
      [resourceType]: resourceId
    });
  };

  // 타이머 조절 핸들러
  const handleTimerDecrease = () => {
    setPaymentPage(prev => ({
      ...prev,
      timerSeconds: Math.max((prev.timerSeconds || 30) - 1, 1)
    }));
  };

  const handleTimerIncrease = () => {
    setPaymentPage(prev => ({
      ...prev,
      timerSeconds: (prev.timerSeconds || 30) + 1
    }));
  };

  // 저장 이벤트 리스너
  useEffect(() => {
    const eventName = `savePageDesign_payment`;

    const saveHandler = async (event) => {
      try {
        setIsLoading(true);
        const dataToSave = {
          ...paymentPage,
          themeId: event.detail.themeId,
          themePageMasterId: paymentPage.themePageMaster?.id || paymentPage.themePageMasterId,
          pageType: 'payment'
        };
        
        console.log("저장할 데이터:", dataToSave);
        const isSucceed = await designApi.savePaymentPage(event.detail.themeId, dataToSave);

        if(isSucceed) {
          alert("저장되었습니다.");
        } else {
          alert("저장에 실패했습니다.");
        }
      } catch (error) {
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    document.addEventListener(eventName, saveHandler);
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [paymentPage, themeId]);

  return (
    <div className="design-settings">
      {/* 언어 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">언어 선택</h4>
        <div className="option-select-container">
          <select
            className="option-select"
            value={paymentPage.languageCode || "ko"}
            onChange={(e) => handleInputChange('languageCode', e.target.value)}
          >
            <option value="ko">한국어</option>
            <option value="en">영어</option>
            <option value="jp">일본어</option>
            <option value="cn">중국어</option>
          </select>
          <i className="ri-arrow-down-s-line select-arrow"></i>
        </div>
      </div>

      {/* 배경 이미지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 이미지</h4>
        <ResourceUploadField
          label=""
          resourceId={paymentPage.backgroundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("backgroundResourceId", id)}
          acceptTypes="image/*"
        />
      </div>

      {/* 배경 음악 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 음악</h4>
        <ResourceUploadField
          label=""
          resourceId={paymentPage.backgroundMusicResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("backgroundMusicResourceId", id)}
          acceptTypes="audio/*"
        />
      </div>

      {/* 안내 메시지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">안내 메시지</h4>
        <div className="settings-row">
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="결제 수단을 선택해주세요" 
              className="text-input"
              value={paymentPage.messageText || ""}
              onChange={(e) => handleInputChange('messageText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={paymentPage.messageColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={paymentPage.messageColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('messageColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 총 금액 표시 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">총 금액 표시</h4>
        <div className="settings-row">
          <div className="settings-label">라벨 텍스트</div>
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="총 금액" 
              value={paymentPage.totalAmountText || "총 금액"}
              onChange={(e) => handleInputChange('totalAmountText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">라벨 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={paymentPage.totalAmountColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={paymentPage.totalAmountColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('totalAmountColor', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">금액 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={paymentPage.priceDisplayColor || "#FFFF00"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={paymentPage.priceDisplayColor || "#FFFF00"}
              onChange={(e) => handleInputChange('priceDisplayColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 결제 수단 활성화 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">결제 수단 활성화</h4>
        <div className="settings-row">
          <div className="settings-label">현금 결제</div>
          <div className="toggle-switch-container">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={paymentPage.cashPaymentEnabled || false}
                onChange={(e) => handleInputChange('cashPaymentEnabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">카드 결제</div>
          <div className="toggle-switch-container">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={paymentPage.cardPaymentEnabled || false}
                onChange={(e) => handleInputChange('cardPaymentEnabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">쿠폰 결제</div>
          <div className="toggle-switch-container">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={paymentPage.couponPaymentEnabled || false}
                onChange={(e) => handleInputChange('couponPaymentEnabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">QR 결제</div>
          <div className="toggle-switch-container">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={paymentPage.qrPaymentEnabled || false}
                onChange={(e) => handleInputChange('qrPaymentEnabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* 현금 결제 버튼 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">현금 결제 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">기본 상태</div>
          <ResourceUploadField
            label=""
            resourceId={paymentPage.cashButtonResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("cashButtonResourceId", id)}
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">선택 상태</div>
          <ResourceUploadField
            label=""
            resourceId={paymentPage.cashButtonSelectedResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("cashButtonSelectedResourceId", id)}
            acceptTypes="image/*"
          />
        </div>
      </div>

      {/* 카드 결제 버튼 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">카드 결제 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">기본 상태</div>
          <ResourceUploadField
            label=""
            resourceId={paymentPage.cardButtonResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("cardButtonResourceId", id)}
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">선택 상태</div>
          <ResourceUploadField
            label=""
            resourceId={paymentPage.cardButtonSelectedResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("cardButtonSelectedResourceId", id)}
            acceptTypes="image/*"
          />
        </div>
      </div>

      {/* 쿠폰 결제 버튼 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">쿠폰 결제 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">기본 상태</div>
          <ResourceUploadField
            label=""
            resourceId={paymentPage.couponButtonResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("couponButtonResourceId", id)}
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">선택 상태</div>
          <ResourceUploadField
            label=""
            resourceId={paymentPage.couponButtonSelectedResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("couponButtonSelectedResourceId", id)}
            acceptTypes="image/*"
          />
        </div>
      </div>

      {/* QR 결제 버튼 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">QR 결제 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">기본 상태</div>
          <ResourceUploadField
            label=""
            resourceId={paymentPage.qrButtonResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("qrButtonResourceId", id)}
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">선택 상태</div>
          <ResourceUploadField
            label=""
            resourceId={paymentPage.qrButtonSelectedResourceId}
            onResourceUploaded={(id) => handleResourceUploaded("qrButtonSelectedResourceId", id)}
            acceptTypes="image/*"
          />
        </div>
      </div>

      {/* 타이머 설정 섹션 */}
      {/* <div className="settings-box">
        <h4 className="settings-title">타이머 설정</h4>
        <div className="settings-row">
          <div className="settings-label">타이머 시간</div>
          <div className="timer-control-container">
            <button 
              className="timer-btn" 
              onClick={handleTimerDecrease}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="timer-input" 
              value={paymentPage.timerSeconds || 30}
              onChange={(e) => handleInputChange('timerSeconds', parseInt(e.target.value) || 1)}
            />
            <button 
              className="timer-btn"
              onClick={handleTimerIncrease}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="timer-unit">초</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">타임 오버 이동 페이지</div>
          <div className="page-select-container">
            <select 
              className="page-select"
              value={paymentPage.timerTimeoutRedirectUrl || "start"}
              onChange={(e) => handleInputChange('timerTimeoutRedirectUrl', e.target.value)}
            >
              <option value="start">시작화면</option>
              <option value="amount">수량선택</option>
              <option value="angle">앵글선택</option>
              <option value="layout">레이아웃</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">타이머 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={paymentPage.timerDisplayColor || "#FF0000"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={paymentPage.timerDisplayColor || "#FF0000"}
              onChange={(e) => handleInputChange('timerDisplayColor', e.target.value)}
            />
          </div>
        </div>
      </div> */}

      {/* 효과음 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">효과음</h4>
        <ResourceUploadField
          label="버튼 클릭 효과음"
          resourceId={paymentPage.buttonClickSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("buttonClickSoundResourceId", id)}
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="결제 완료 효과음"
          resourceId={paymentPage.paymentSuccessSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("paymentSuccessSoundResourceId", id)}
          acceptTypes="audio/*"
        />
        <ResourceUploadField
          label="오류 효과음"
          resourceId={paymentPage.errorSoundResourceId}
          onResourceUploaded={(id) => handleResourceUploaded("errorSoundResourceId", id)}
          acceptTypes="audio/*"
        />
      </div>
    </div>
  );
};

export default PaymentSetting;